import { defineStore } from 'pinia'
import Pusher, { Channel } from 'pusher-js'

interface Message {
  id: string
  senderId: string
  receiverId: string
  content: string
  timestamp: Date
  status: 'sent' | 'delivered' | 'read'
}

export const useMessageStore = defineStore('messages', {
  state: () => ({
    pusher: null as Pusher | null,
    channel: null as Channel | null,
    messages: [] as Message[],
    isLoading: false,
    error: null as string | null,
    currentConversationId: null as string | null,
  }),

  actions: {
    // Initialize Pusher connection
    initializePusher(apiKey: string, cluster: string, authEndpoint: string) {
      if (this.pusher) return // Already initialized

      this.pusher = new Pusher(apiKey, {
        cluster,
        authEndpoint,
        auth: {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      })

      // You might want to bind to connection state changes
      this.pusher.connection.bind('state_change', (states: any) => {
        console.log('Pusher connection state changed:', states.current)
      })
    },

    // Subscribe to a channel
    subscribeToChannel(channelName: string) {
      if (!this.pusher) {
        throw new Error('Pusher not initialized')
      }

      // Unsubscribe from previous channel if exists
      if (this.channel) {
        this.unsubscribeFromChannel()
      }

      this.channel = this.pusher.subscribe(channelName)

      // Bind to message events
      this.channel.bind('new-message', (data: Message) => {
        this.handleIncomingMessage(data)
      })

      this.channel.bind('message-read', (data: { messageId: string }) => {
        this.updateMessageStatus(data.messageId, 'read')
      })

      this.channel.bind('message-delivered', (data: { messageId: string }) => {
        this.updateMessageStatus(data.messageId, 'delivered')
      })
    },

    // Unsubscribe from current channel
    unsubscribeFromChannel() {
      if (this.channel) {
        this.channel.unbind_all()
        this.channel.unsubscribe()
        this.channel = null
      }
    },

    // Handle incoming messages
    handleIncomingMessage(message: Message) {
      this.messages.push(message)
      // You might want to sort messages by timestamp here
      this.sortMessages()
    },

    // Update message status
    updateMessageStatus(messageId: string, status: 'sent' | 'delivered' | 'read') {
      const message = this.messages.find((m) => m.id === messageId)
      if (message) {
        message.status = status
      }
    },

    // Sort messages by timestamp
    sortMessages() {
      this.messages.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    },

    // Send a message
    async sendMessage(receiverId: string, content: string) {
      if (!this.pusher) {
        throw new Error('Pusher not initialized')
      }

      this.isLoading = true
      this.error = null

      try {
        const response = await fetch('https://your-api-endpoint.com/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            receiverId,
            content,
          }),
        })

        if (!response.ok) {
          throw new Error('Failed to send message')
        }

        const message = await response.json()
        this.messages.push(message)
        this.sortMessages()
        return message
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to send message'
        throw err
      } finally {
        this.isLoading = false
      }
    },

    // Fetch conversation history
    async fetchConversation(conversationId: string) {
      this.isLoading = true
      this.error = null
      this.currentConversationId = conversationId

      try {
        const response = await fetch(`https://your-api-endpoint.com/conversations/${conversationId}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        })

        if (!response.ok) {
          throw new Error('Failed to fetch conversation')
        }

        const messages = await response.json()
        this.messages = messages
        this.sortMessages()
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to fetch conversation'
        throw err
      } finally {
        this.isLoading = false
      }
    },

    // Clean up on logout
    cleanup() {
      this.unsubscribeFromChannel()
      if (this.pusher) {
        this.pusher.disconnect()
        this.pusher = null
      }
      this.messages = []
      this.currentConversationId = null
    },
  },

  getters: {
    getMessages: (state) => state.messages,
    getUnreadCount: (state) => state.messages.filter((m) => m.status !== 'read').length,
    isLoadingMessages: (state) => state.isLoading,
    getError: (state) => state.error,
  },
})
